import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls /version.json and reloads the page when a new deployment is detected.
 *
 * - Compares deployed buildTime against the compiled-in __BUILD_TIME__
 * - Pauses polling when the tab is hidden or on the /game route
 * - Silently swallows fetch errors (offline, network blip)
 */
export function useVersionCheck(): void {
  const location = useLocation();
  const isGameRoute = location.pathname === '/game';
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Don't poll during active gameplay
    if (isGameRoute) return;

    async function check(): Promise<void> {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data: { buildTime?: string } = await res.json();
        if (data.buildTime && data.buildTime !== __BUILD_TIME__) {
          window.location.reload();
        }
      } catch {
        // Network error — retry next interval
      }
    }

    // Check immediately on mount / route change
    check();

    intervalRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isGameRoute]);
}
